import { orgSelectors } from 'ducks/orgs'
import { getHardwareProductImageUrlCandidates } from 'elements/hardwareSelectorV2/hardwareProductImage'
import { ComponentLikeType } from 'elements/hardwareSelectorV2/types'
import useImagePrioritization from 'elements/hardwareSelectorV2/useImagePrioritization'
import { Box, CircularProgress, ImagePlaceHolderIcon, styled, Tooltip, Typography } from 'opensolar-ui'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { formatCurrencyWithSymbol } from 'util/misc'
import { useBundleDeal } from './hooks/useBundleDeal'
import { Deal } from './types'

const TooltipBody = styled(Box)({
  padding: 4,
  minWidth: 240,
  maxWidth: 280,
})

const ItemRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 0',
}))

const Thumbnail = styled('img')({
  width: 36,
  height: 36,
  objectFit: 'contain',
  flexShrink: 0,
})

const ItemTitle = styled(Typography)({
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: '1 1 auto',
  minWidth: 0,
})

const ItemSku = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.grey[600],
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

const TotalRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 8,
  paddingTop: 8,
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  fontSize: 13,
  fontWeight: 600,
}))

const BundleItemThumbnail = ({ product, distributorKey }: { product: ComponentLikeType; distributorKey: string }) => {
  const distributorEntry = product.ordering_v2?.find((d) => d.distributor === distributorKey)
  const imageUrls = [...getHardwareProductImageUrlCandidates(product), distributorEntry?.image_url]
  const { currentImageUrl, handleImageError } = useImagePrioritization(imageUrls)

  if (!currentImageUrl) {
    return (
      <Box width={36} height={36} display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
        <ImagePlaceHolderIcon height="28" width="28" fill="#ADACB0" />
      </Box>
    )
  }

  return <Thumbnail src={currentImageUrl} alt={product.code} onError={handleImageError} loading="lazy" />
}

const BundleDealTooltipTitle = ({ deal, currentProductCode }: { deal: Deal; currentProductCode?: string }) => {
  const translate = useTranslate()
  const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)
  const distributorKey = deal.distributor
  const { isFetching, sortedProducts, discountedTotal, bundleTotal } = useBundleDeal({ deal, currentProductCode })
  const displayTotal = Math.min(discountedTotal, bundleTotal)

  if (isFetching) {
    return (
      <TooltipBody display="flex" justifyContent="center" minHeight={60} alignItems="center">
        <CircularProgress size={24} />
      </TooltipBody>
    )
  }

  if (!sortedProducts.length) {
    return (
      <TooltipBody>
        <Typography variant="body2">{translate('No bundle products found.')}</Typography>
      </TooltipBody>
    )
  }

  return (
    <TooltipBody>
      {sortedProducts.map((product) => {
        const distributorEntry = product.ordering_v2?.find((d) => d.distributor === distributorKey)
        const title = distributorEntry?.title || product.name || product.code
        return (
          <ItemRow key={product.id}>
            <BundleItemThumbnail product={product} distributorKey={distributorKey} />
            <Box flex="1 1 auto" minWidth={0}>
              <ItemTitle title={title}>{title}</ItemTitle>
              <ItemSku title={product.code}>{product.code}</ItemSku>
            </Box>
            <Typography variant="body2" style={{ fontSize: 12 }}>
              ×1
            </Typography>
          </ItemRow>
        )
      })}
      <TotalRow>
        <span>{translate('Bundle price')}</span>
        <span>{formatCurrencyWithSymbol(displayTotal, currencySymbol, undefined, 2)}</span>
      </TotalRow>
    </TooltipBody>
  )
}

type BundleDealTooltipProps = {
  deal: Deal
  currentProductCode?: string
  children: React.ReactElement
}

const BundleDealTooltip = ({ deal, currentProductCode, children }: BundleDealTooltipProps) => (
  <Tooltip
    interactive
    placement="bottom-start"
    enterDelay={0}
    leaveDelay={200}
    title={<BundleDealTooltipTitle deal={deal} currentProductCode={currentProductCode} />}
  >
    {children}
  </Tooltip>
)

export default BundleDealTooltip
